import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector, ReversibleEffector } from './EffectorBase';
import { PropertyTypes, EasingFunctionTypes, EasingFunctions } from '../../../globals';
import ColorProperty from '../../../components/properties/ColorProperty';
import MaterialAsset from '../../MaterialAsset';
import Text3D from '../../Text3D';
import { TimelineFragment } from '../TimelineFragment';
import SceneObjectUtils from '../../utilities/SceneObjectUtils';
import store from '../../../state/store';

interface EffectorObjectColorSaveData
{
	targetValue: string;
	reversed: boolean;
	easing: EasingFunctionTypes;
	loopCount: number;
}

export default class EffectorObjectColor extends EffectorBase<EffectorObjectColor> implements LoopableEffector, ReversibleEffector
{
	targetValue: THREE.Color;
	inputColorHex: string;
	originalColors: Map<THREE.Object3D, THREE.Color>;
	reversed: boolean;
	easing: EasingFunctionTypes;
	loopCount: number;

	constructor(fragment : TimelineFragment)
	{
		super(fragment);

		this.inputColorHex = store.getState().preferencesModule.editorPreferences.defaultSetObjectColor || "#ff00ff";
		this.targetValue = new THREE.Color();
		this.originalColors = new Map();
		this.reversed = false;
		this.easing = EasingFunctionTypes.Linear;
		this.loopCount = 1;

		this.setTargetValue(this.inputColorHex);
	}

	save = () : EffectorObjectColorSaveData =>
	{
		const output = {
			targetValue: this.targetValue.getHexString(),
			reversed: this.reversed,
			easing: this.easing,
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorObjectColorSaveData) =>
	{
		this.setTargetValue(savedData.targetValue);
		if (savedData.reversed) this.reversed = savedData.reversed;
		else if ("isReverse" in savedData) this.reversed = savedData.isReverse as boolean; //backwards comp
		if (savedData.easing) this.easing = savedData.easing;
		if (savedData.loopCount) this.loopCount = savedData.loopCount;
	};

	setTargetValue = (color: string) =>
	{
		this.inputColorHex = color;
		if (color[0] === '#')
		{
			color = color.substr(1);
			color = '0x' + color;
		}
		else if (color.length === 6)
		{
			color = '0x' + color;
		}

		this.targetValue.setHex(parseInt(color));
	}

	copyValues = (sourceEffector: EffectorObjectColor) =>
	{
		this.targetValue.copy(sourceEffector.targetValue);
		this.inputColorHex = sourceEffector.inputColorHex;
		this.reversed = sourceEffector.reversed;
		this.easing = sourceEffector.easing;
		this.loopCount = sourceEffector.loopCount;
	}

	resetState = () =>
	{
		if (this.fragment.previousTime)
		{
			this.forEachTargetObject(function(targetObject)
			{
				targetObject.traverse((child) =>
				{
					if (child.userData.overrideColorFactor !== undefined) delete child.userData.overrideColorFactor;
					if (child.userData.overrideColor) SceneObjectUtils.applyColorChangeEffect(child);
				});
			});
		}
	}

	applyEffect = (object: THREE.Object3D, fragmentTime: number) =>
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
		if (materialAsset || (object instanceof Text3D))
		{
			if (object.userData.overrideColor === undefined) object.userData.overrideColor = new THREE.Color();
		}
		
		if (object.userData.overrideColor)
		{
			object.userData.overrideColor.copy(this.targetValue);
			object.userData.overrideColorFactor = fragmentTime;
			SceneObjectUtils.applyColorChangeEffect(object);
		}
	}

	update = (fragmentTime: number) =>
	{
		if (!fragmentTime || (this.reversed && fragmentTime === 1)) return;

		const t = EasingFunctions.pingPong(fragmentTime);
		this.forEachTargetObject((targetObject) =>
		{
			targetObject.traverse((child) =>
			{
				this.applyEffect(child, t);
			});
		});
	}

	onAdded = () =>
	{
		
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Target Color",
				PropertyTypes.Color,
				<ColorProperty
					value={this.inputColorHex}
				/>,
				() => {return this.inputColorHex;},
				(v) => {this.setTargetValue(v);},
			),
		];
	}
}
